import { ConnectError, createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { getListCompanyArtifactQueryKey } from '@/api/analyzer.hook'
import { getListControlsQueryKey } from '@/api/control.hook'
import {
  getArtifactRevisionDownloadUrl,
  getArtifactRevisionPreviewUrl,
  updateArtifactSubcategory,
} from '@/gen/artifacts/store/v1/store_service-ArtifactStoreService_connectquery'
import {
  deleteOrgArtifact,
  getArtifactDownloadUrl,
  getArtifactPreviewUrl,
} from '@/gen/artifacts/store/v1/store_service-OrgArtifactStoreService_connectquery'
import { OrgArtifactStoreService } from '@/gen/artifacts/store/v1/store_service_pb'
import { Artifact, ArtifactClassification } from '@/gen/artifacts/v1/model_pb'
import {
  listCompanyArtifact,
  listUserSourcedArtifact,
} from '@/gen/inventory/v1/artifact_service-ArtifactService_connectquery'
import { ListUserSourcedArtifactRequest } from '@/gen/inventory/v1/artifact_service_pb'

import { artifactSubcategoryLabel } from '@/const/label'

import { useTrackCallback } from '@/lib/analytics/events'
import { defaultTransport } from '@/lib/connect/connect-transport'
import { type BundleSelection, isDownloadableArtifact } from '@/lib/file/artifact-download'

import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

const DOWNLOAD_FAILED_TITLE = 'Download failed'
const DOWNLOAD_FAILED_FALLBACK = 'Something went wrong. Try again.'

export const getListUserSourcedArtifactQueryKey = (
  artifactSubcategory?: ListUserSourcedArtifactRequest['artifactSubcategory'],
  companyId?: ListUserSourcedArtifactRequest['companyId'],
) => {
  return createConnectQueryKey({
    schema: listUserSourcedArtifact,
    cardinality: 'finite',
    transport: defaultTransport,
    input: { artifactSubcategory, companyId },
  })
}

export const useDeleteOrgArtifact = () => {
  const queryClient = useQueryClient()
  const trackArtifactDelete = useTrackCallback('artifact.delete')

  const { toast } = useToast()
  return useMutation(deleteOrgArtifact, {
    onSuccess: (_data, variables) => {
      trackArtifactDelete({ artifactId: variables.artifactId })
      queryClient.invalidateQueries({ queryKey: getListUserSourcedArtifactQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompanyArtifactQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      toast({
        variant: 'success',
        title: 'Artifact deleted successfully',
      })
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'An error has occurred',
      })
    },
  })
}

export const useListUserSourcedArtifact = (
  companyId?: string,
  artifactSubcategory?: keyof typeof artifactSubcategoryLabel,
) => {
  return useQuery(
    listUserSourcedArtifact,
    { companyId, artifactSubcategory },
    { refetchInterval: 15000 },
  )
}

export const useListCompanyArtifact = (companyId?: string, enabled = true) => {
  return useQuery(
    listCompanyArtifact,
    { companyId },
    { refetchInterval: 15000, enabled: enabled && !!companyId },
  )
}

/** Counts-only variant of the list query, for badges and summary cards. */
export const useListCompanyArtifactCounts = (companyId?: string, enabled = true) => {
  return useQuery(
    listCompanyArtifact,
    { companyId, countOnly: true },
    { refetchInterval: 15000, enabled: enabled && !!companyId },
  )
}

/**
 * Downloads a single artifact via a presigned link. Mutations rather than
 * queries: the link expires in five minutes, so a cached one goes dead.
 */
export const useDownloadArtifact = () => {
  const { toast } = useToast()

  // Signed with an attachment disposition, so this downloads rather than navigates.
  const onSuccess = ({ url }: { url: string }) => window.location.assign(url)
  const onError = () =>
    toast({
      variant: 'destructive',
      title: DOWNLOAD_FAILED_TITLE,
      description: 'Could not prepare the download. Try again.',
    })

  const orgDocument = useMutation(getArtifactDownloadUrl, { onSuccess, onError })
  const publicRevision = useMutation(getArtifactRevisionDownloadUrl, { onSuccess, onError })

  const downloadArtifact = (artifact: Artifact) => {
    if (!isDownloadableArtifact(artifact)) {
      return
    }

    // Not artifact.id: for a public artifact that is the Artifact row, while the
    // oneof carries the revision the RPC and bucket key are on.
    const id = artifact.artifact.value as string

    if (artifact.classification === ArtifactClassification.PUBLIC) {
      publicRevision.mutate({ id })
      return
    }
    orgDocument.mutate({ id })
  }

  return {
    downloadArtifact,
    isDownloadPending: orgDocument.isPending || publicRevision.isPending,
  }
}

// connect-query generates no hook for streaming methods. Going through the
// shared transport keeps sessionTokenInterceptor's refresh and 401 retry.
const bundleClient = createClient(OrgArtifactStoreService, defaultTransport)

/** The server words its rejections for the user, so they are shown as-is. */
export const buildDownloadErrorDescription = (error: ConnectError): string =>
  error.rawMessage || DOWNLOAD_FAILED_FALLBACK

const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Deferred: WebKit can cancel the download if the URL is revoked in the same
  // task as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

// Streaming is for the server's memory, not ours -- a Blob is how the browser
// takes a file it did not navigate to, so the bytes land here either way.
const collectArchive = async (selection: BundleSelection): Promise<Blob> => {
  const chunks: Uint8Array[] = []
  for await (const response of bundleClient.downloadArtifactBundle(selection)) {
    chunks.push(response.chunk)
  }
  return new Blob(chunks as BlobPart[], { type: 'application/zip' })
}

/**
 * Downloads several artifacts as one zip.
 *
 * Distinct from useDownloadArtifact rather than a plural version of it: there is
 * no presigned link for an archive that does not exist yet, so the server builds
 * it on the fly and streams it through us.
 */
export const useArtifactBulkDownload = () => {
  const { toast } = useToast()
  const [isDownloading, setIsDownloading] = useState(false)

  const download = useCallback(
    async (selection: BundleSelection) => {
      // isDownloading also blocks a double-click firing two downloads.
      if (selection.artifactIds.length + selection.revisionIds.length === 0 || isDownloading) return

      setIsDownloading(true)
      try {
        const archive = await collectArchive(selection)
        saveBlob(archive, `artifacts-${new Date().toISOString().slice(0, 10)}.zip`)
      } catch (error) {
        toast({
          variant: 'destructive',
          title: DOWNLOAD_FAILED_TITLE,
          // catch hands us `unknown`; ConnectError.from is the transport's own
          // narrowing, so it stays at this boundary.
          description: buildDownloadErrorDescription(ConnectError.from(error)),
        })
      } finally {
        setIsDownloading(false)
      }
    },
    [toast, isDownloading],
  )

  return { download, isDownloading }
}

export const getGetArtifactPreviewUrlQueryKey = (artifactsId: string) => {
  return createConnectQueryKey({
    schema: getArtifactPreviewUrl,
    input: { id: artifactsId },
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const useGetArtifactPreviewUrl = (artifactsId: string, enabled = true) => {
  const staleTimeMilliseconds = 300000 // 5 minutes it's the expiration time of the preview URL

  return useQuery(
    getArtifactPreviewUrl,
    { id: artifactsId },
    {
      staleTime: staleTimeMilliseconds,
      enabled,
    },
  )
}

export const useGetArtifactRevisionPreviewUrl = (artifactsId: string, enabled = true) => {
  const staleTimeMilliseconds = 300000 // 5 minutes it's the expiration time of the preview URL

  return useQuery(
    getArtifactRevisionPreviewUrl,
    { id: artifactsId },
    {
      staleTime: staleTimeMilliseconds,
      enabled,
    },
  )
}

export const useUpdateArtifactSubcategory = () => {
  const queryClient = useQueryClient()
  return useMutation(updateArtifactSubcategory, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListUserSourcedArtifactQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListCompanyArtifactQueryKey() })
    },
  })
}
